import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import {
  calcularEstadoLineaCosto,
  CONCEPTOS_COSTO_PROYECTO,
  ESTADO_LINEA_COSTO,
  getConceptoCostoLabel,
  getCostosProyecto,
  getCuotaCountsPorCosto,
  getCuotasDeCosto,
  getResumenVinculosPorCostos,
  guardarCostoProyecto,
  eliminarCostoProyecto,
  getCostoProyectoPorId,
  totalConIvaCosto,
} from "../../utils/costosProyecto";
import { getLocale } from "../../utils/getLocale";
import { getProveedores, getNombreProveedor } from "../../utils/proveedores";
import { useCurrency } from "../../utils/useCurrency";
import { exigirFechaValida, MODOS_VALIDACION } from "../../utils/validarFechaDocumento";
import KebabMenu from "../KebabMenu";
import { Loader } from "../Loader";
import PlanCuotasCostoModal from "./PlanCuotasCostoModal";
import VincularAnticipoCostoModal from "./VincularAnticipoCostoModal";
import VincularFacturaCostoModal from "./VincularFacturaCostoModal";
import VinculosCostoModal from "./VinculosCostoModal";
// Mismas clases CSS / mismos estilos que el modal Inversión (diseño aprobado: copia fiel, 10.161).
// Se importa el módulo de EdificiosTable en vez de duplicar las reglas (10.1) — CSS Modules permite
// reusar el objeto de clases compiladas desde otro componente sin copiar una sola línea de SCSS.
// El ANCHO del modal es la única excepción: tiene su propio CSS Module (`styles`, abajo) en vez de
// `.modal-inversion` — con 12 columnas de detalle esa clase quedaba angosta; `.modal-inversion`
// sigue intacta para la ventana Inversión (2026-10-04, 10.6).
import stylesInversion from "../EdificiosTable/EdificiosTable.module.scss";
import styles from "./CarteleriaCostosModal.module.scss";

// Badge del Estado DERIVADO de una línea (Fase 6, 2026-10-04 — `calcularEstadoLineaCosto`,
// src/utils/costosProyecto.js). Mismos colores ámbar/verde que `getBadgeEstado` de la ventana
// Inversión de Edificios (EdificiosTable/index.js: planificado → ámbar #fff3cd, ejecutado → verde
// #d4edda) — 'previsto'/'pagado' heredan esos dos; 'parcial'/'facturado' son dos tonos de azul
// intermedios (cobertura en curso), ninguno existe aún en Inversión.
const ESTADO_LINEA_COSTO_BADGE = {
  [ESTADO_LINEA_COSTO.PREVISTO]: { label: "Previsto", bg: "#fff3cd", color: "#856404" },
  [ESTADO_LINEA_COSTO.PARCIAL]: { label: "Parcial", bg: "#cfe2ff", color: "#084298" },
  [ESTADO_LINEA_COSTO.FACTURADO]: { label: "Facturado", bg: "#9ec5fe", color: "#052c65" },
  [ESTADO_LINEA_COSTO.PAGADO]: { label: "Pagado", bg: "#d4edda", color: "#155724" },
};

// ─── Ventana "Costos" de un proyecto de Cartelería (Fase 2 — frontend) ────────────────────────
// Copia fiel de la ventana Inversión de Edificios (docs/decisiones/2026-10-03-costos-carteleria-
// fase2.md): misma estructura (zona fija de desglose + detalle colapsable + tabla densa), mismas
// clases CSS. Entidad y escritura 100% nuevas y aisladas: NO toca `activos`, `plan_pagos_activos`,
// Futuros ni el ER (10.13 líneas 9 del prompt de fase). Toda escritura pasa por las RPCs
// `guardar_costo_proyecto` / `eliminar_costo_proyecto` (10.190) — nunca un INSERT/UPDATE directo.
//
// En esta fase SOLO existe la columna "Previsto" (el total con IVA de cada concepto). Las columnas
// "Pagado"/"Pendiente" llegan en una fase posterior, cuando exista el vínculo real con facturas y
// pagos — no se simulan con datos falsos acá (ver diseño §D-10).
const CarteleriaCostosModal = ({ negocio, nombreProyecto, onClose, userId }) => {
  const [costos, setCostos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cuotaCounts, setCuotaCounts] = useState({});
  const [vinculosResumen, setVinculosResumen] = useState({}); // costoId -> { vinculadoNeto, folios[] }
  const [loading, setLoading] = useState(true);
  const [mostrarDetalle, setMostrarDetalle] = useState(true);
  const [nuevaLinea, setNuevaLinea] = useState(null);
  const [editandoLinea, setEditandoLinea] = useState(null);
  const [planCuotasCosto, setPlanCuotasCosto] = useState(null); // costo completo, o null
  const [vincularFacturaCosto, setVincularFacturaCosto] = useState(null); // costo completo, o null
  const [vincularAnticipoCosto, setVincularAnticipoCosto] = useState(null); // costo completo, o null
  const [verVinculosCosto, setVerVinculosCosto] = useState(null); // costo completo, o null

  const { formatAmount, convert, monedasDisponibles, loadingRates } = useCurrency();
  const pais = negocio.pais;
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";

  const cargarCostos = async () => {
    setLoading(true);
    try {
      const data = await getCostosProyecto(negocio.id);
      setCostos(data);
      const ids = data.map((c) => c.id);
      setCuotaCounts(ids.length > 0 ? await getCuotaCountsPorCosto(ids) : {});
      // Fase 4 (vínculo con documentos, 2026-10-04): columnas "Vinculado"/"Remanente". Fase 6: trae
      // también el detalle (`vinculos`) que alimenta el badge de Estado.
      setVinculosResumen(ids.length > 0 ? await getResumenVinculosPorCostos(data, pais) : {});
    } catch (err) {
      toast.error("Error al cargar los costos: " + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  // Patch puntual del resumen de vínculos de UN costo (10.234) — tras vincular/desvincular, nunca
  // se recarga la lista completa del proyecto. Necesita el `proveedor_id` de la línea (lo trae
  // `costos`, ya en memoria — nunca una query extra, 10.167) para resolver la fuente única del
  // saldo (`getSaldosPorFacturaProveedor`, dentro de `getResumenVinculosPorCostos`).
  const refrescarVinculosDeCosto = async (costoId) => {
    const costo = costos.find((c) => c.id === costoId);
    if (!costo) return;
    const resumen = await getResumenVinculosPorCostos([costo], pais);
    setVinculosResumen((prev) => ({
      ...prev,
      [costoId]: resumen[costoId] || { vinculadoNeto: 0, folios: [], vinculos: [] },
    }));
  };

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const provs = await getProveedores(pais);
        if (vivo) setProveedores(provs);
      } catch (err) {
        toast.error("Error al cargar proveedores: " + (err.message || err));
      }
    })();
    cargarCostos();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negocio.id]);

  // Monto en inputs: separador de miles en vivo (display, SIN símbolo de moneda) — mismo patrón que
  // EdificiosTable/index.js (formatMontoInput/parseMontoInput).
  const formatMontoInput = (raw) => (raw === "" || raw == null ? "" : Number(raw).toLocaleString(getLocale(pais)));
  const parseMontoInput = (str) => str.replace(/\D/g, "");

  // Fecha de visualización dd/mm/aaaa — mismo patrón ya usado en otras tablas del proyecto
  // (TablaCanonica.formatFechaDefault, Empleados/index.js, MisCotizaciones, PropuestasExternas,
  // CotizacionesGeneradas): toLocaleDateString con day/month 2-digit + year numeric. El "T12:00:00"
  // evita que el parseo de "YYYY-MM-DD" corra un día hacia atrás por TZ (mismo guard que
  // EdificiosTable.formatFecha). Antes se mostraba `fecha_prevista` crudo ("YYYY-MM-DD"), que
  // partía en 2 líneas dentro de la columna angosta.
  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "—";
    return new Date(`${fechaStr}T12:00:00`).toLocaleDateString(getLocale(pais), {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Desglose por concepto — ÚNICA cifra de esta fase: "Previsto" = Σ total CON IVA (brutoCapex,
  // fuente única 10.1/10.19/10.27) de cada costo, convertido a moneda local antes de sumar (10.185:
  // nunca raw-sum entre monedas distintas). TC vivo (convert) — son montos PREVISTOS, no devengados.
  const desglosePorConcepto = useMemo(() => {
    const map = new Map();
    for (const c of costos) {
      const { total } = totalConIvaCosto(c.monto, c.moneda, pais, c.exento_iva);
      const totalLocal = c.moneda === monedaLocal ? total : convert(total, c.moneda, monedaLocal);
      map.set(c.concepto, (map.get(c.concepto) || 0) + totalLocal);
    }
    const filas = [...map.entries()]
      .map(([concepto, previsto]) => ({ concepto, previsto }))
      .sort((a, b) => b.previsto - a.previsto);
    const total = filas.reduce((s, f) => s + f.previsto, 0);
    return { filas, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costos, pais, monedaLocal]);

  // Patch puntual por ID tras guardar (10.234): nunca se recarga la lista completa del proyecto.
  const aplicarPatchCosto = async (costoId) => {
    const fresco = await getCostoProyectoPorId(costoId);
    setCostos((prev) => {
      const existe = prev.some((c) => c.id === fresco.id);
      return existe ? prev.map((c) => (c.id === fresco.id ? fresco : c)) : [fresco, ...prev];
    });
    setCuotaCounts((prev) => ({ ...prev, [costoId]: prev[costoId] ?? 0 }));
  };

  const handleGuardarLinea = async () => {
    if (!nuevaLinea?.concepto) return toast.error("El concepto es obligatorio");
    if (!nuevaLinea?.proveedor_id) return toast.error("El proveedor es obligatorio");
    if (!nuevaLinea?.monto) return toast.error("El monto (neto) es obligatorio");
    if (!nuevaLinea?.fecha_prevista) return toast.error("La fecha es obligatoria");
    try {
      exigirFechaValida({ fecha_prevista: nuevaLinea.fecha_prevista }, "fecha_prevista", {
        modo: MODOS_VALIDACION.DATO_DOCUMENTO,
      });
      const r = await guardarCostoProyecto({
        negocioId: negocio.id,
        proveedorId: nuevaLinea.proveedor_id,
        concepto: nuevaLinea.concepto,
        descripcion: nuevaLinea.descripcion || "",
        monto: Number(nuevaLinea.monto),
        moneda: nuevaLinea.moneda || monedaLocal,
        exentoIva: !!nuevaLinea.exento_iva,
        estado: "fecha_unica",
        fechaUnica: nuevaLinea.fecha_prevista,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
      });
      await aplicarPatchCosto(r.costo_id);
      setNuevaLinea(null);
      toast.success("Costo agregado");
    } catch (err) {
      toast.error("Error al guardar: " + (err.message || err));
    }
  };

  const handleEditarLinea = (c) => {
    setNuevaLinea(null);
    setEditandoLinea({
      id: c.id,
      concepto: c.concepto,
      proveedor_id: c.proveedor_id,
      descripcion: c.descripcion || "",
      moneda: c.moneda,
      monto: String(c.monto),
      exento_iva: !!c.exento_iva,
      fecha_prevista: c.fecha_prevista || "",
      tieneCuotas: (cuotaCounts[c.id] || 0) > 0,
    });
  };

  const handleGuardarEdicion = async () => {
    const e = editandoLinea;
    if (!e?.concepto) return toast.error("El concepto es obligatorio");
    if (!e?.proveedor_id) return toast.error("El proveedor es obligatorio");
    if (!e?.monto) return toast.error("El monto (neto) es obligatorio");
    if (!e.tieneCuotas && !e.fecha_prevista) return toast.error("La fecha es obligatoria");
    try {
      // Con plan de cuotas vigente, el header se reedita en estado 'fecha_unica' SOLO si el monto no
      // cambió lo suficiente para romper Σcuotas=monto — si rompe, la RPC lo rechaza con su mensaje
      // real (10.177): el camino correcto para tocar el monto de un costo con plan es "Plan de
      // cuotas". Acá siempre se reenvía 'plan' con las cuotas intactas (la RPC las conserva por id).
      let payload;
      if (e.tieneCuotas) {
        const cuotasActuales = await getCuotasDeCosto(e.id);
        payload = {
          estado: "plan",
          cuotas: cuotasActuales.map((cu) => ({
            id: cu.id,
            monto_cuota: Number(cu.monto_cuota),
            fecha_vencimiento: cu.fecha_vencimiento,
          })),
        };
      } else {
        exigirFechaValida({ fecha_prevista: e.fecha_prevista }, "fecha_prevista", {
          modo: MODOS_VALIDACION.DATO_DOCUMENTO,
        });
        payload = { estado: "fecha_unica", fechaUnica: e.fecha_prevista };
      }
      const r = await guardarCostoProyecto({
        costoId: e.id,
        proveedorId: e.proveedor_id,
        concepto: e.concepto,
        descripcion: e.descripcion || "",
        monto: Number(e.monto),
        moneda: e.moneda,
        exentoIva: !!e.exento_iva,
        usuarioId: Number.isFinite(Number(userId)) ? Number(userId) : null,
        ...payload,
      });
      await aplicarPatchCosto(r.costo_id);
      setEditandoLinea(null);
      toast.success("Costo actualizado");
    } catch (err) {
      toast.error("Error al actualizar: " + (err.message || err));
    }
  };

  const handleEliminarLinea = async (id) => {
    if (!window.confirm("¿Eliminar este costo previsto?")) return;
    try {
      await eliminarCostoProyecto(id);
      setCostos((prev) => prev.filter((c) => c.id !== id));
      toast.success("Costo eliminado");
    } catch (err) {
      toast.error("Error al eliminar: " + (err.message || err));
    }
  };

  const nombreProveedor = (c) => (c.proveedor ? getNombreProveedor(c.proveedor) : "—");

  return (
    <Modal show onHide={onClose} dialogClassName={styles.modalCostosCarteleria}>
      <Modal.Header closeButton>
        <Modal.Title>
          Costos — {(nombreProyecto || "").toUpperCase()}{" "}
          <span style={{ fontSize: "0.65em", color: "#6c757d", fontWeight: 400 }}>({negocio.modalidad || "—"})</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ display: "flex", flexDirection: "column", maxHeight: "85vh", overflow: "hidden" }}>
        {loading || loadingRates ? (
          <Loader />
        ) : (
          <>
            {/* Zona FIJA: desglose por concepto con fila TOTAL (no scrollea). Única columna de esta
                fase: "Previsto" (total CON IVA). Pagado/Pendiente llegan con la Fase 3 (vínculo real
                con facturas y pagos) — no se simulan acá. */}
            <div style={{ flexShrink: 0 }}>
              {costos.length > 0 && desglosePorConcepto.filas.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: "0.75rem", color: "#6c757d", textTransform: "uppercase", marginBottom: 4 }}>
                    Desglose por concepto
                  </div>
                  <table className={`table table-sm ${stylesInversion.densaInversion}`}>
                    <thead className="table-light">
                      <tr>
                        <th style={{ textAlign: "left" }}>Concepto</th>
                        <th style={{ textAlign: "right" }}>Previsto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {desglosePorConcepto.filas.map((f) => (
                        <tr key={f.concepto}>
                          <td style={{ textAlign: "left" }}>{getConceptoCostoLabel(f.concepto)}</td>
                          <td style={{ textAlign: "right" }}>{formatAmount(f.previsto, monedaLocal)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 700, borderTop: "2px solid #adb5bd" }}>
                        <td style={{ textAlign: "left" }}>TOTAL</td>
                        <td style={{ textAlign: "right" }}>{formatAmount(desglosePorConcepto.total, monedaLocal)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            {/* Toggle del detalle (fijo): expande la lista de costos + alta */}
            <div style={{ flexShrink: 0, display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
              <button
                className="btn btn-link btn-sm"
                style={{ padding: 0, textDecoration: "none" }}
                onClick={() => setMostrarDetalle((v) => !v)}
              >
                {mostrarDetalle ? "▾" : "▸"} Ver detalle de costos ({costos.length})
              </button>
              {mostrarDetalle && (
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() =>
                    setNuevaLinea({
                      concepto: "",
                      descripcion: "",
                      moneda: monedaLocal,
                      monto: "",
                      fecha_prevista: "",
                      proveedor_id: null,
                      exento_iva: false,
                    })
                  }
                  disabled={!!nuevaLinea || !!editandoLinea}
                >
                  + Agregar línea
                </button>
              )}
            </div>

            {/* Zona DETALLE (desplegable): scrollea interno, toma el alto restante */}
            {mostrarDetalle && (
              <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                <table
                  className={`table table-sm table-bordered ${stylesInversion.densaInversion} ${stylesInversion.tablaDetalleInversion}`}
                >
                  {/* Anchos autoritativos por <colgroup> (10.161). Base de Inversión + 2 columnas
                      nuevas de la Fase 4 (vínculo con documentos, 2026-10-04): Vinculado·Remanente,
                      en NETO (misma unidad que la cobertura del costo, 10.246) justo después de Neto:
                      Concepto·Proveedor·Descripción·Moneda·Neto·Vinculado·Remanente·IVA·Total·Fecha·
                      Estado·Acciones = 100% */}
                  <colgroup>
                    <col style={{ width: "9%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "5%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: "8%" }} />
                    <col style={{ width: "9%" }} />
                  </colgroup>
                  <thead className="table-dark">
                    <tr>
                      <th style={{ textAlign: "left" }}>Concepto</th>
                      <th style={{ textAlign: "left" }}>Proveedor</th>
                      <th style={{ textAlign: "left" }}>Descripción</th>
                      <th>Moneda</th>
                      <th style={{ textAlign: "right" }}>Neto</th>
                      <th style={{ textAlign: "right" }}>Vinculado</th>
                      <th style={{ textAlign: "right" }}>Remanente</th>
                      <th style={{ textAlign: "right" }}>IVA</th>
                      <th style={{ textAlign: "right" }}>Total</th>
                      <th>Fecha</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costos.length === 0 && !nuevaLinea && (
                      <tr>
                        <td colSpan={12} style={{ textAlign: "center", color: "#999", padding: 30 }}>
                          Sin costos registrados para este proyecto
                        </td>
                      </tr>
                    )}
                    {costos.map((c) =>
                      editandoLinea?.id === c.id ? (
                        <tr key={c.id} style={{ background: "#e8f4fd" }}>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={editandoLinea.concepto || ""}
                              onChange={(e) => setEditandoLinea((p) => ({ ...p, concepto: e.target.value }))}
                            >
                              <option value="">Concepto...</option>
                              {CONCEPTOS_COSTO_PROYECTO.map((cc) => (
                                <option key={cc} value={cc}>
                                  {getConceptoCostoLabel(cc)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={editandoLinea.proveedor_id || ""}
                              onChange={(e) =>
                                setEditandoLinea((p) => ({ ...p, proveedor_id: Number(e.target.value) || null }))
                              }
                              title="Proveedor"
                            >
                              <option value="">Proveedor…</option>
                              {proveedores.map((prov) => (
                                <option key={prov.id} value={prov.id}>
                                  {getNombreProveedor(prov)}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              placeholder="Descripción"
                              value={editandoLinea.descripcion || ""}
                              onChange={(e) => setEditandoLinea((p) => ({ ...p, descripcion: e.target.value }))}
                            />
                          </td>
                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={editandoLinea.moneda || monedaLocal}
                              onChange={(e) => setEditandoLinea((p) => ({ ...p, moneda: e.target.value }))}
                              style={{ paddingRight: 22 }}
                            >
                              {monedasDisponibles.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              className="form-control form-control-sm"
                              type="text"
                              inputMode="numeric"
                              placeholder="Monto"
                              value={formatMontoInput(editandoLinea.monto)}
                              onChange={(e) =>
                                setEditandoLinea((p) => ({ ...p, monto: parseMontoInput(e.target.value) }))
                              }
                              style={{ textAlign: "right" }}
                              disabled={editandoLinea.tieneCuotas}
                              title={
                                editandoLinea.tieneCuotas
                                  ? "Con plan de cuotas: ajustá el monto desde Plan de cuotas"
                                  : undefined
                              }
                            />
                          </td>
                          {/* Vinculado/Remanente: NO editables acá — se mueven solo por "Vincular factura"/
                              "Desvincular" del kebab (Fase 4). Mismo dato que la fila de solo-lectura. */}
                          {(() => {
                            const resumen = vinculosResumen[editandoLinea.id] || { vinculadoNeto: 0, folios: [] };
                            const remanente = Number(editandoLinea.monto || 0) - resumen.vinculadoNeto;
                            return (
                              <>
                                <td style={{ textAlign: "right" }} title={resumen.folios.join(", ") || undefined}>
                                  {resumen.vinculadoNeto > 0
                                    ? formatAmount(resumen.vinculadoNeto, editandoLinea.moneda)
                                    : "—"}
                                </td>
                                <td style={{ textAlign: "right" }}>
                                  {formatAmount(Math.max(0, remanente), editandoLinea.moneda)}
                                </td>
                              </>
                            );
                          })()}
                          {(() => {
                            const { iva, total, aplica } = totalConIvaCosto(
                              editandoLinea.monto,
                              editandoLinea.moneda,
                              pais,
                              editandoLinea.exento_iva
                            );
                            return (
                              <>
                                <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                                  <label
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 4,
                                      fontSize: "0.7rem",
                                      cursor: "pointer",
                                      justifyContent: "flex-end",
                                    }}
                                    title="Sin IVA (exento)"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!editandoLinea.exento_iva}
                                      onChange={(e) =>
                                        setEditandoLinea((p) => ({ ...p, exento_iva: e.target.checked }))
                                      }
                                    />
                                    Sin IVA
                                  </label>
                                  <div style={{ color: aplica ? undefined : "#999", marginTop: 2 }}>
                                    {aplica ? formatAmount(iva, editandoLinea.moneda) : "—"}
                                  </div>
                                </td>
                                <td style={{ textAlign: "right", fontWeight: 600, verticalAlign: "middle" }}>
                                  {formatAmount(total, editandoLinea.moneda)}
                                </td>
                              </>
                            );
                          })()}
                          <td>
                            {editandoLinea.tieneCuotas ? (
                              <span style={{ fontSize: "0.75rem", color: "#888" }}>Ver plan de cuotas</span>
                            ) : (
                              <input
                                className="form-control form-control-sm"
                                type="date"
                                value={editandoLinea.fecha_prevista || ""}
                                onChange={(e) => setEditandoLinea((p) => ({ ...p, fecha_prevista: e.target.value }))}
                                style={!editandoLinea.fecha_prevista ? { borderColor: "#dc3545" } : undefined}
                              />
                            )}
                          </td>
                          <td>
                            <span
                              style={{
                                fontSize: "0.7rem",
                                padding: "2px 6px",
                                borderRadius: 3,
                                background: "#fff3cd",
                                color: "#856404",
                              }}
                            >
                              Previsto
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                              <button
                                className="btn btn-success btn-sm"
                                style={{ fontSize: "10px" }}
                                title="Guardar cambios"
                                onClick={handleGuardarEdicion}
                              >
                                ✓
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ fontSize: "10px" }}
                                title="Cancelar"
                                onClick={() => setEditandoLinea(null)}
                              >
                                ×
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        (() => {
                          // Resumen + badge de Estado (Fase 6) — computados UNA vez por fila y
                          // reusados en las celdas Vinculado/Remanente/Estado y en el atenuado de
                          // la fila entera (10.1: nunca reimplementar el remanente por celda).
                          const resumen = vinculosResumen[c.id] || { vinculadoNeto: 0, folios: [], vinculos: [] };
                          const remanente = Number(c.monto) - resumen.vinculadoNeto;
                          const vinculosConFecha = (resumen.vinculos || []).map((v) =>
                            v.tipo === "anticipo" ? { ...v, fechaLabel: formatFecha(v.fecha) } : v
                          );
                          const { estado, folioDisplay } = calcularEstadoLineaCosto(
                            c.monto,
                            resumen.vinculadoNeto,
                            vinculosConFecha
                          );
                          const badge = ESTADO_LINEA_COSTO_BADGE[estado];
                          // Fila atenuada cuando el remanente ya es 0 (tolerancia 1, idéntica a
                          // `calcularEstadoLineaCosto`/`calcularDisponibleVinculo`) — mismo lenguaje
                          // visual de opacidad reducida que ya usa el proyecto para ítems resueltos
                          // (FlujoCajaV2, p.ej. opacity 0.75 para contenido secundario de una fila).
                          const atenuada = remanente <= 1;
                          return (
                            <tr key={c.id} style={atenuada ? { opacity: 0.75 } : undefined}>
                              <td style={{ textAlign: "left" }}>{getConceptoCostoLabel(c.concepto)}</td>
                              <td
                                className={stylesInversion.celdaTruncada}
                                style={{ textAlign: "left" }}
                                title={nombreProveedor(c)}
                              >
                                {nombreProveedor(c)}
                              </td>
                              <td
                                className={stylesInversion.celdaTruncada}
                                style={{ textAlign: "left" }}
                                title={c.descripcion || undefined}
                              >
                                {c.descripcion || "—"}
                              </td>
                              <td>{c.moneda}</td>
                              <td style={{ textAlign: "right" }}>{formatAmount(Number(c.monto), c.moneda)}</td>
                              {/* Vinculado/Remanente (Fase 4, 2026-10-04) — Σ neto vigente de
                                  costos_proyecto_vinculos, con el/los folio(s) en el tooltip. */}
                              <td style={{ textAlign: "right" }} title={resumen.folios.join(", ") || undefined}>
                                {resumen.vinculadoNeto > 0 ? formatAmount(resumen.vinculadoNeto, c.moneda) : "—"}
                              </td>
                              <td style={{ textAlign: "right" }}>{formatAmount(Math.max(0, remanente), c.moneda)}</td>
                              {(() => {
                                const { iva, total, aplica } = totalConIvaCosto(c.monto, c.moneda, pais, c.exento_iva);
                                return (
                                  <>
                                    <td style={{ textAlign: "right", color: aplica ? undefined : "#999" }}>
                                      {aplica ? formatAmount(iva, c.moneda) : "—"}
                                    </td>
                                    <td style={{ textAlign: "right", fontWeight: 600 }}>
                                      {formatAmount(total, c.moneda)}
                                    </td>
                                  </>
                                );
                              })()}
                              <td style={{ whiteSpace: "nowrap" }}>{formatFecha(c.fecha_prevista)}</td>
                              <td>
                                <span
                                  style={{
                                    fontSize: "0.7rem",
                                    padding: "2px 6px",
                                    borderRadius: 3,
                                    background: badge.bg,
                                    color: badge.color,
                                  }}
                                >
                                  {badge.label}
                                </span>
                                {folioDisplay && (
                                  <div style={{ marginTop: 3, fontSize: "0.68rem", color: "#555" }}>{folioDisplay}</div>
                                )}
                                {cuotaCounts[c.id] > 0 && (
                                  <div style={{ marginTop: 3, fontSize: "0.68rem", color: "#2c3e50", fontWeight: 600 }}>
                                    plan de cuotas ({cuotaCounts[c.id]})
                                  </div>
                                )}
                              </td>
                              <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                <KebabMenu
                                  items={[
                                    { label: "Editar", onClick: () => handleEditarLinea(c) },
                                    { label: "Plan de cuotas", onClick: () => setPlanCuotasCosto(c) },
                                    { label: "Vincular factura", onClick: () => setVincularFacturaCosto(c) },
                                    { label: "Vincular pago ya hecho", onClick: () => setVincularAnticipoCosto(c) },
                                    {
                                      label: "Desvincular",
                                      disabled: !(vinculosResumen[c.id]?.vinculadoNeto > 0),
                                      tooltip: !(vinculosResumen[c.id]?.vinculadoNeto > 0)
                                        ? "Esta línea no tiene ninguna factura ni anticipo vinculado"
                                        : undefined,
                                      onClick: () => setVerVinculosCosto(c),
                                    },
                                    { label: "Eliminar", danger: true, onClick: () => handleEliminarLinea(c.id) },
                                  ]}
                                />
                              </td>
                            </tr>
                          );
                        })()
                      )
                    )}

                    {nuevaLinea && (
                      <tr style={{ background: "#e8f4fd" }}>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={nuevaLinea.concepto || ""}
                            onChange={(e) => setNuevaLinea((p) => ({ ...p, concepto: e.target.value }))}
                          >
                            <option value="">Concepto...</option>
                            {CONCEPTOS_COSTO_PROYECTO.map((cc) => (
                              <option key={cc} value={cc}>
                                {getConceptoCostoLabel(cc)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {/* Proveedor OBLIGATORIO (10.177) — mismo patrón que Inversión. Lista YA
                              filtrada por país del proyecto (getProveedores(negocio.pais), 10.281). */}
                          <select
                            className="form-select form-select-sm"
                            style={!nuevaLinea.proveedor_id ? { borderColor: "#dc3545" } : undefined}
                            value={nuevaLinea.proveedor_id || ""}
                            onChange={(e) =>
                              setNuevaLinea((p) => ({ ...p, proveedor_id: Number(e.target.value) || null }))
                            }
                            title="Proveedor (obligatorio)"
                          >
                            <option value="">Proveedor…</option>
                            {proveedores.map((prov) => (
                              <option key={prov.id} value={prov.id}>
                                {getNombreProveedor(prov)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            placeholder="Descripción"
                            value={nuevaLinea.descripcion || ""}
                            onChange={(e) => setNuevaLinea((p) => ({ ...p, descripcion: e.target.value }))}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={nuevaLinea.moneda || monedaLocal}
                            onChange={(e) => setNuevaLinea((p) => ({ ...p, moneda: e.target.value }))}
                            style={{ paddingRight: 22 }}
                          >
                            {monedasDisponibles.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            inputMode="numeric"
                            placeholder="Monto"
                            value={formatMontoInput(nuevaLinea.monto)}
                            onChange={(e) => setNuevaLinea((p) => ({ ...p, monto: parseMontoInput(e.target.value) }))}
                            style={{ textAlign: "right" }}
                          />
                        </td>
                        {/* Una línea nueva todavía no existe en la base → sin vínculos posibles. */}
                        <td style={{ textAlign: "right", color: "#999" }}>—</td>
                        <td style={{ textAlign: "right" }}>
                          {formatMontoInput(nuevaLinea.monto)
                            ? formatAmount(Number(nuevaLinea.monto || 0), nuevaLinea.moneda || monedaLocal)
                            : "—"}
                        </td>
                        {(() => {
                          const { iva, total, aplica } = totalConIvaCosto(
                            nuevaLinea.monto,
                            nuevaLinea.moneda,
                            pais,
                            nuevaLinea.exento_iva
                          );
                          return (
                            <>
                              <td style={{ textAlign: "right", verticalAlign: "middle" }}>
                                <label
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    fontSize: "0.7rem",
                                    cursor: "pointer",
                                    justifyContent: "flex-end",
                                  }}
                                  title="Sin IVA (exento)"
                                >
                                  <input
                                    type="checkbox"
                                    checked={!!nuevaLinea.exento_iva}
                                    onChange={(e) => setNuevaLinea((p) => ({ ...p, exento_iva: e.target.checked }))}
                                  />
                                  Sin IVA
                                </label>
                                <div style={{ color: aplica ? undefined : "#999", marginTop: 2 }}>
                                  {aplica ? formatAmount(iva, nuevaLinea.moneda) : "—"}
                                </div>
                              </td>
                              <td style={{ textAlign: "right", fontWeight: 600, verticalAlign: "middle" }}>
                                {formatAmount(total, nuevaLinea.moneda)}
                              </td>
                            </>
                          );
                        })()}
                        <td>
                          <input
                            className="form-control form-control-sm"
                            type="date"
                            title="Fecha prevista (obligatoria)"
                            value={nuevaLinea.fecha_prevista || ""}
                            onChange={(e) => setNuevaLinea((p) => ({ ...p, fecha_prevista: e.target.value }))}
                            style={!nuevaLinea.fecha_prevista ? { borderColor: "#dc3545" } : undefined}
                          />
                        </td>
                        <td>
                          <span
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: "#fff3cd",
                              color: "#856404",
                            }}
                          >
                            Previsto
                          </span>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>
                          <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                            <button
                              className="btn btn-success btn-sm"
                              style={{ fontSize: "10px" }}
                              onClick={handleGuardarLinea}
                            >
                              ✓
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{ fontSize: "10px" }}
                              onClick={() => setNuevaLinea(null)}
                            >
                              ×
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Modal.Body>

      {/* Plan de cuotas de un costo — sub-modal propio (NO el de Inversión/activos: estado y RPC
          distintos). Al guardar, patch puntual del costo tocado (10.234). */}
      {planCuotasCosto && (
        <PlanCuotasCostoModal
          show={!!planCuotasCosto}
          costo={planCuotasCosto}
          pais={pais}
          userId={userId}
          onHide={() => setPlanCuotasCosto(null)}
          onSaved={async (costoId) => {
            setPlanCuotasCosto(null);
            await aplicarPatchCosto(costoId);
            setCuotaCounts(await getCuotaCountsPorCosto(costos.map((c) => c.id)));
          }}
        />
      )}

      {/* Vincular factura / Desvincular — Fase 4 (vínculo con documentos, 2026-10-04). Patch
          puntual del resumen de vínculos de SOLO la línea tocada (10.234) — nunca recarga la lista
          completa del proyecto. */}
      {vincularFacturaCosto && (
        <VincularFacturaCostoModal
          show={!!vincularFacturaCosto}
          costo={vincularFacturaCosto}
          negocio={negocio}
          userId={userId}
          onHide={() => setVincularFacturaCosto(null)}
          onVinculado={async () => {
            const costoId = vincularFacturaCosto.id;
            setVincularFacturaCosto(null);
            await refrescarVinculosDeCosto(costoId);
          }}
        />
      )}
      {verVinculosCosto && (
        <VinculosCostoModal
          show={!!verVinculosCosto}
          costo={verVinculosCosto}
          userId={userId}
          onHide={() => setVerVinculosCosto(null)}
          onCambio={async () => {
            await refrescarVinculosDeCosto(verVinculosCosto.id);
          }}
        />
      )}

      {/* Vincular pago ya hecho (anticipo) — Fase 5 (2026-10-04). Mismo patrón de patch puntual
          (10.234) que "Vincular factura". */}
      {vincularAnticipoCosto && (
        <VincularAnticipoCostoModal
          show={!!vincularAnticipoCosto}
          costo={vincularAnticipoCosto}
          negocio={negocio}
          userId={userId}
          onHide={() => setVincularAnticipoCosto(null)}
          onVinculado={async () => {
            const costoId = vincularAnticipoCosto.id;
            setVincularAnticipoCosto(null);
            await refrescarVinculosDeCosto(costoId);
          }}
        />
      )}
    </Modal>
  );
};

export default CarteleriaCostosModal;
